import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { useLocation, Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
    Container,
    Row,
    Col,
    Card,
    Form,
    Button,
    Table,
    Badge,
    Alert,
    Spinner,
    Modal,
    Tabs,
    Tab,
    Pagination
} from 'react-bootstrap';
import {
    Package,
    MapPin,
    AlertCircle,
    Search,
    UserPlus,
    Truck,
    CheckCircle,
    Calendar,
    Phone,
    FileText,
    XCircle,
    Edit,
    IndianRupee,
    Filter,
    Download,
    User
} from 'lucide-react';
import "bootstrap/dist/css/bootstrap.min.css";
import { getCanReturns, exportCanReturns, getAvailableAgentsForReturn, assignCanReturnAgent, updateCanReturnStatus } from '../../actions/canReturn';
import { getServiceableAreas } from '../../actions/serviceableArea';

const PAGE_SIZE = 20;

const STATUSES = ['pending', 'assigned', 'completed', 'refunded', 'cancelled'];

const INITIAL_FILTERS = {
    transactionId: '',
    phone: '',
    agentName: '',
    serviceableArea: '',
    startDate: '',
    endDate: '',
    latest: 'true'
};

const STATUS_TRANSITIONS = {
    pending: ['cancelled'],
    assigned: ['completed', 'cancelled'],
    completed: ['refunded']
};

const CanReturns = ({
    canReturns,
    pages,
    counts,
    agents,
    serviceableAreas,
    loading,
    error,
    getCanReturns,
    exportCanReturns,
    getServiceableAreas,
    getAvailableAgentsForReturn,
    assignCanReturnAgent,
    updateCanReturnStatus
}) => {
    const [activeTab, setActiveTab] = useState('pending');
    const [formData, setFormData] = useState(INITIAL_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState(INITIAL_FILTERS);
    const [dateError, setDateError] = useState('');
    const [exportLoading, setExportLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [selectedCanReturn, setSelectedCanReturn] = useState(null);
    const [selectedAgent, setSelectedAgent] = useState('');
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [statusTarget, setStatusTarget] = useState(null);
    const [newStatus, setNewStatus] = useState('');
    const [statusComment, setStatusComment] = useState('');

    const location = useLocation();
    const userFilter = new URLSearchParams(location.search).get('user');

    useEffect(() => {
        document.title = 'Can Returns';
    }, []);

    useEffect(() => {
        getServiceableAreas();
    }, [getServiceableAreas]);

    const fetchCurrentView = useCallback(() => (
        getCanReturns({
            user: userFilter,
            status: activeTab,
            ...appliedFilters,
            page,
            limit: PAGE_SIZE
        })
    ), [getCanReturns, userFilter, activeTab, appliedFilters, page]);

    useEffect(() => {
        fetchCurrentView();
    }, [fetchCurrentView]);

    const onChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const onSubmit = (e) => {
        e.preventDefault();
        if (formData.startDate && formData.endDate && formData.endDate < formData.startDate) {
            setDateError('End date must be after start date');
            return;
        }
        setDateError('');
        setAppliedFilters(formData);
        setPage(1);
    };

    const clearFilters = () => {
        setDateError('');
        setFormData(INITIAL_FILTERS);
        setAppliedFilters(INITIAL_FILTERS);
        setPage(1);
    };

    const handleExport = async () => {
        setExportLoading(true);
        try {
            await exportCanReturns({ user: userFilter, status: activeTab, ...appliedFilters });
        } finally {
            setExportLoading(false);
        }
    };

    const formatDate = (dateString) => {
        return dateString ? new Date(dateString).toLocaleString() : 'N/A';
    };

    const formatPhone = (phone) => {
        if (phone && phone.length === 10) {
            return `+91 ${phone}`;
        }
        return phone;
    };

    const getStatusBadge = (status) => {
        const variants = {
            pending: 'warning',
            assigned: 'info',
            completed: 'primary',
            refunded: 'success',
            cancelled: 'secondary'
        };
        return <Badge bg={variants[status] || 'secondary'}>{status.toUpperCase()}</Badge>;
    };

    const handleAssign = (canReturn) => {
        setSelectedCanReturn(canReturn);
        getAvailableAgentsForReturn(canReturn._id);
        setShowAssignModal(true);
    };

    const confirmAssign = () => {
        if (selectedCanReturn && selectedAgent) {
            assignCanReturnAgent(selectedCanReturn._id, selectedAgent).then(fetchCurrentView);
            setShowAssignModal(false);
            setSelectedAgent('');
            setSelectedCanReturn(null);
        }
    };

    const handleOpenStatusModal = (canReturn) => {
        setStatusTarget(canReturn);
        setNewStatus('');
        setStatusComment('');
        setShowStatusModal(true);
    };

    const confirmStatusUpdate = () => {
        if (statusTarget && newStatus) {
            updateCanReturnStatus(statusTarget._id, newStatus, statusComment).then(fetchCurrentView);
            setShowStatusModal(false);
            setStatusTarget(null);
            setNewStatus('');
            setStatusComment('');
        }
    };

    const renderAgentRows = (list, badgeLabel, badgeVariant) => (
        (list || []).map(agent => (
            <tr
                key={agent._id}
                role="button"
                onClick={() => setSelectedAgent(agent._id)}
                className={selectedAgent === agent._id ? 'table-primary' : ''}
                style={{ cursor: 'pointer' }}
            >
                <td style={{ verticalAlign: 'middle' }} className="text-center" onClick={(e) => e.stopPropagation()}>
                    <Form.Check.Input
                        type="radio"
                        name="selectedAgent"
                        value={agent._id}
                        checked={selectedAgent === agent._id}
                        onChange={(e) => setSelectedAgent(e.target.value)}
                        className="m-0"
                    />
                </td>
                <td className="fw-semibold">{agent.name}</td>
                <td>{agent.phone}</td>
                <td><Badge bg={badgeVariant}>{badgeLabel}</Badge></td>
                <td>{agent.activeDeliveries || 0}</td>
            </tr>
        ))
    );

    const renderPagination = () => {
        const totalPages = pages;
        if (!totalPages || totalPages <= 1) return null;

        const startPage = Math.max(1, Math.min(page - 2, totalPages - 4));
        const endPage = Math.min(totalPages, startPage + 4);
        const items = [];
        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item key={number} active={number === page} onClick={() => setPage(number)}>
                    {number}
                </Pagination.Item>
            );
        }

        return (
            <div className="d-flex justify-content-center py-3">
                <Pagination className="mb-0">
                    <Pagination.Prev disabled={page === 1} onClick={() => setPage(page - 1)} />
                    {items}
                    <Pagination.Next disabled={page === totalPages} onClick={() => setPage(page + 1)} />
                </Pagination>
            </div>
        );
    };

    const renderTable = (status) => {
        if (status !== activeTab) return null;
        const list = canReturns || [];
        return (
            <Card className="shadow-sm">
                <Card.Body className="p-0">
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                        </div>
                    ) : list.length === 0 ? (
                        <Alert variant="info" className="m-3">
                            No {status.replace('_', ' ')} can returns found.
                        </Alert>
                    ) : (
                        <Table responsive hover className="mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th>Transaction ID</th>
                                    <th>Customer</th>
                                    <th>Address</th>
                                    <th>Cans</th>
                                    <th>Pickup Date</th>
                                    <th>Status</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {list.map(canReturn => (
                                    <tr key={canReturn._id}>
                                        <td className="fw-bold">
                                            <FileText size={14} className="me-1 text-muted" />
                                            {canReturn.transactionId}
                                        </td>
                                        <td>
                                            <div>
                                                <div className="fw-semibold">{canReturn.user?.name || 'Deleted User'}</div>
                                                <small className="text-muted">
                                                    <Phone size={12} className="me-1" />
                                                    {formatPhone(canReturn.user?.phone || 'N/A')}
                                                </small>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-start">
                                                <MapPin size={14} className="me-1 mt-1 text-muted" />
                                                <small>{canReturn.deliveryAddress?.fullAddress || 'N/A'}</small>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center">
                                                <Package size={14} className="me-1 text-muted" />
                                                {canReturn.numberOfCans}
                                            </div>
                                        </td>
                                        <td>
                                            <small className="text-muted">
                                                <Calendar size={12} className="me-1" />
                                                {formatDate(canReturn.scheduledFor)}
                                            </small>
                                        </td>
                                        <td>{getStatusBadge(canReturn.status)}</td>
                                        <td>
                                            {canReturn.status === 'pending' && (
                                                <Button variant="primary" size="sm" onClick={() => handleAssign(canReturn)}>
                                                    <UserPlus size={14} className="me-1" />
                                                    Assign
                                                </Button>
                                            )}
                                            {(STATUS_TRANSITIONS[canReturn.status] || []).length > 0 && (
                                                <Button variant="outline-primary" size="sm" onClick={() => handleOpenStatusModal(canReturn)}>
                                                    <Edit size={14} className="me-1" />
                                                    Update Status
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                    {renderPagination()}
                </Card.Body>
            </Card>
        );
    };

    if (error) {
        return (
            <Container className="py-4">
                <Alert variant="warning">
                    <AlertCircle size={20} className="me-2" />
                    {error}
                </Alert>
            </Container>
        );
    }

    return (
        <Container fluid className="py-4">
            <Row className="mb-4">
                <Col>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                            <h2 className="mb-0">Can Returns</h2>
                            <p className="text-muted mb-0">Customer can pickup requests</p>
                        </div>
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={handleExport}
                            disabled={exportLoading}
                            className="d-flex align-items-center"
                            title="Exports the current tab with the applied filters"
                        >
                            {exportLoading ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-1" />
                                    Exporting...
                                </>
                            ) : (
                                <>
                                    <Download size={16} className="me-1" />
                                    Export CSV
                                </>
                            )}
                        </Button>
                    </div>

                    {userFilter && (
                        <Alert variant="info" className="d-flex justify-content-between align-items-center">
                            <span>Showing can returns for one user only.</span>
                            <Link to="/inventory/v1.2/can-returns" className="btn btn-outline-secondary btn-sm">
                                Clear filter
                            </Link>
                        </Alert>
                    )}

                    <Card className="shadow-sm mb-4">
                        <Card.Header className="bg-primary text-white">
                            <h5 className="mb-0">
                                <Filter className="me-2" />
                                Search Filters
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            <Form onSubmit={onSubmit}>
                                <Row className="g-3">
                                    <Col md={6} lg={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-bold">
                                                <FileText className="me-2" size={16} />
                                                Transaction ID
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="transactionId"
                                                value={formData.transactionId}
                                                onChange={onChange}
                                                placeholder="Enter transaction ID"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6} lg={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-bold">
                                                <Phone className="me-2" size={16} />
                                                Phone Number
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={onChange}
                                                placeholder="Enter phone number"
                                                maxLength={10}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6} lg={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-bold">
                                                <User className="me-2" size={16} />
                                                Agent Name
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="agentName"
                                                value={formData.agentName}
                                                onChange={onChange}
                                                placeholder="Enter agent name"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6} lg={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-bold">
                                                <MapPin className="me-2" size={16} />
                                                Serviceable Area
                                            </Form.Label>
                                            <Form.Select
                                                name="serviceableArea"
                                                value={formData.serviceableArea}
                                                onChange={onChange}
                                            >
                                                <option value="">All Serviceable Areas</option>
                                                {serviceableAreas && serviceableAreas.map(area => (
                                                    <option key={area._id} value={area._id}>
                                                        {area.name}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6} lg={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-bold">
                                                <Calendar className="me-2" size={16} />
                                                Pickup Start Date
                                            </Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="startDate"
                                                value={formData.startDate}
                                                onChange={onChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6} lg={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-bold">
                                                <Calendar className="me-2" size={16} />
                                                Pickup End Date
                                            </Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="endDate"
                                                value={formData.endDate}
                                                onChange={onChange}
                                                isInvalid={!!dateError}
                                            />
                                            <Form.Control.Feedback type="invalid">{dateError}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6} lg={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-bold">
                                                <i className="fas fa-sort me-2"></i>
                                                Sort Order
                                            </Form.Label>
                                            <Form.Select
                                                name="latest"
                                                value={formData.latest}
                                                onChange={onChange}
                                            >
                                                <option value="true">Newest First</option>
                                                <option value="false">Oldest First</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={12} className="d-flex align-items-end">
                                        <div className="d-flex gap-2 w-100">
                                            <Button type="submit" variant="primary" disabled={loading} className="flex-fill">
                                                {loading ? (
                                                    <>
                                                        <Spinner size="sm" className="me-2" />
                                                        Searching...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Search className="me-2" size={16} />
                                                        Apply Filters
                                                    </>
                                                )}
                                            </Button>
                                            <Button type="button" variant="outline-secondary" onClick={clearFilters} disabled={loading}>
                                                <XCircle className="me-2" size={16} />
                                                Clear
                                            </Button>
                                        </div>
                                    </Col>
                                </Row>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Tabs activeKey={activeTab} onSelect={(k) => {
                setActiveTab(k);
                setPage(1);
            }} className="mb-4">
                {STATUSES.map(status => (
                    <Tab
                        key={status}
                        eventKey={status}
                        title={
                            <span>
                                {status === 'pending' && <AlertCircle size={16} className="me-2" />}
                                {status === 'assigned' && <Truck size={16} className="me-2" />}
                                {status === 'completed' && <CheckCircle size={16} className="me-2" />}
                                {status === 'refunded' && <IndianRupee size={16} className="me-2" />}
                                {status === 'cancelled' && <XCircle size={16} className="me-2" />}
                                {status.replace('_', ' ')} ({counts?.[status] || 0})
                            </span>
                        }
                    >
                        {renderTable(status)}
                    </Tab>
                ))}
            </Tabs>

            <Modal show={showAssignModal} onHide={() => setShowAssignModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Assign Pickup Agent</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedCanReturn && (
                        <div>
                            <Card className="mb-3">
                                <Card.Header>
                                    <h6 className="mb-0">Pickup Details</h6>
                                </Card.Header>
                                <Card.Body>
                                    <Row>
                                        <Col md={6}>
                                            <p><strong>Transaction ID:</strong> {selectedCanReturn.transactionId}</p>
                                            <p><strong>Customer:</strong> {selectedCanReturn.user?.name || 'Deleted User'}</p>
                                            <p><strong>Phone:</strong> {formatPhone(selectedCanReturn.user?.phone || 'N/A')}</p>
                                        </Col>
                                        <Col md={6}>
                                            <p><strong>Address:</strong> {selectedCanReturn.deliveryAddress?.fullAddress || 'N/A'}</p>
                                            <p><strong>Cans:</strong> {selectedCanReturn.numberOfCans}</p>
                                            <p><strong>Pickup Date:</strong> {formatDate(selectedCanReturn.scheduledFor)}</p>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>

                            <h6>Select Agent:</h6>
                            <Table responsive className="mt-2">
                                <thead>
                                    <tr>
                                        <th>Select</th>
                                        <th>Agent Name</th>
                                        <th>Phone</th>
                                        <th>Status</th>
                                        <th>Active Deliveries</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {renderAgentRows(agents.freeAgents, 'AVAILABLE', 'success')}
                                    {renderAgentRows(agents.busyAgents, 'BUSY', 'secondary')}
                                    {renderAgentRows(agents.refillAgents, 'REFILL', 'secondary')}
                                    {renderAgentRows(agents.lowInventoryAgents, 'LOW INVENTORY', 'secondary')}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAssignModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={confirmAssign} disabled={!selectedAgent}>
                        Assign Agent
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <Edit className="me-2" />
                        Update Pickup Status
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">New Status</Form.Label>
                        <Form.Select value={newStatus} onChange={(e) => setNewStatus(e.target.value)}>
                            <option value="">Select Status</option>
                            {(statusTarget ? STATUS_TRANSITIONS[statusTarget.status] || [] : []).map(status => (
                                <option key={status} value={status}>{status.replace('_', ' ').toUpperCase()}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label className="fw-bold">Notes</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={statusComment}
                            onChange={(e) => setStatusComment(e.target.value)}
                            placeholder="Optional notes about this status change..."
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowStatusModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={confirmStatusUpdate} disabled={!newStatus}>
                        Update Status
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

CanReturns.propTypes = {
    canReturns: PropTypes.array,
    pages: PropTypes.number,
    counts: PropTypes.object,
    agents: PropTypes.object,
    serviceableAreas: PropTypes.array,
    loading: PropTypes.bool,
    error: PropTypes.string,
    getCanReturns: PropTypes.func.isRequired,
    exportCanReturns: PropTypes.func.isRequired,
    getServiceableAreas: PropTypes.func.isRequired,
    getAvailableAgentsForReturn: PropTypes.func.isRequired,
    assignCanReturnAgent: PropTypes.func.isRequired,
    updateCanReturnStatus: PropTypes.func.isRequired
};

const mapStateToProps = (state) => ({
    canReturns: state.canReturn.canReturns,
    pages: state.canReturn.pages,
    counts: state.canReturn.counts,
    agents: state.delivery.agents,
    serviceableAreas: state.serviceableArea.serviceableAreas,
    loading: state.canReturn.loading,
    error: state.canReturn.error
});

export default connect(
    mapStateToProps,
    { getCanReturns, exportCanReturns, getServiceableAreas, getAvailableAgentsForReturn, assignCanReturnAgent, updateCanReturnStatus }
)(CanReturns);
